// Pure, isolated sort + group for the Active groups card grid.
//
// Input: a flat Section list + a sort key. Output: a Barn-grouped, sorted structure.
// Barn grouping is preserved in every sort mode — sorting only reorders Sections within
// their Barn; the Barns themselves stay in source order. The module is pure and has no
// Vue/store dependencies so it can be unit-tested in isolation if the surface graduates
// from preview.

import { ATTENTION_SEVERITY } from './needsAttention';
import type { BarnGroup, SectionRow } from './sectionRow';

export type SectionSortKey = 'age' | 'attention' | 'section';

// Default sort is age youngest → oldest, so a newly started Production Group is always first.
export const DEFAULT_SORT_KEY: SectionSortKey = 'age';

// Stable, locale-aware secondary ordering by Section name (numeric so "Section 2" < "Section 10").
function byName(a: SectionRow, b: SectionRow): number {
    return a.sectionName.localeCompare(b.sectionName, undefined, { numeric: true, sensitivity: 'base' });
}

function compareBy(key: SectionSortKey, a: SectionRow, b: SectionRow): number {
    switch (key) {
        case 'age':
            // Youngest → oldest (smallest day-in-Production Group first). Empty / no-data Sections
            // (no day) have no age, so they sort to the end; name breaks ties.
            if (a.day === null && b.day === null) {
                return byName(a, b);
            }
            if (a.day === null) {
                return 1;
            }
            if (b.day === null) {
                return -1;
            }
            return a.day - b.day || byName(a, b);

        case 'section':
            return byName(a, b);

        case 'attention':
        default: {
            // Attention-first, and binary: the rows carrying an active Alarm, then the rest, with
            // name breaking ties on both sides. Empty Sections evaluate to `none` and naturally
            // fall to the bottom. `attentionLevel` is precomputed per row by useSectionRows, so
            // this reads it rather than recomputing it here.
            const severityA = ATTENTION_SEVERITY[a.attentionLevel];
            const severityB = ATTENTION_SEVERITY[b.attentionLevel];
            return severityB - severityA || byName(a, b);
        }
    }
}

export function sortAndGroupSections(sections: SectionRow[], key: SectionSortKey): BarnGroup[] {
    // Group by Barn, preserving first-seen Barn order (matches the source data order).
    const groups = new Map<string, SectionRow[]>();
    for (const section of sections) {
        const existing = groups.get(section.barnName);
        if (existing) {
            existing.push(section);
        } else {
            groups.set(section.barnName, [section]);
        }
    }

    return Array.from(groups, ([barnName, barnSections]) => ({
        barnName,
        barnId: barnSections[0].barnId,
        sections: [...barnSections].sort((a, b) => {
            // The tiers apply to every sort key, including 'section'. They are a priority ordering,
            // not a tie-breaker: the page exists so the Sections needing action stand out, and a
            // Section with no active Production Group has nothing to act on. Ordering strictly by name
            // instead would scatter the handful of live rows among the inactive placeholders, which is
            // exactly the burying this page was built to undo. The sort control's label has to convey
            // that the key orders within those tiers.
            //
            // Tier 1: sections with no active Production Group (inactive) sink to the very bottom.
            const inactive = Number(!a.hasProductionGroup) - Number(!b.hasProductionGroup);
            if (inactive !== 0) return inactive;
            // Tier 2: ghost (empty) rows below active rows within the active tier.
            const ghost = Number(a.isEmpty) - Number(b.isEmpty);
            if (ghost !== 0) return ghost;
            // Tier 3: active sort key.
            return compareBy(key, a, b);
        }),
    }));
}

// The multi-select page groups Sections by Farm first, then by Barn within each
// Farm. Farm order follows first-seen order in the flat list (the page feeds farms in picker order);
// Barn grouping and within-Barn sorting are delegated to `sortAndGroupSections`. `sectionCount` is the
// count of active (non-ghost) Sections, shown next to the farm name in the table's farm header row.
export type FarmGroup = {
    farmId: number;
    farmName: string;
    sectionCount: number;
    barns: BarnGroup[];
};

export function sortAndGroupSectionsByFarm(sections: SectionRow[], key: SectionSortKey): FarmGroup[] {
    const farmOrder: number[] = [];
    const byFarm = new Map<number, SectionRow[]>();
    for (const section of sections) {
        const existing = byFarm.get(section.farmId);
        if (existing) {
            existing.push(section);
        } else {
            byFarm.set(section.farmId, [section]);
            farmOrder.push(section.farmId);
        }
    }

    return farmOrder.map((farmId) => {
        const farmSections = byFarm.get(farmId) ?? [];
        return {
            farmId,
            farmName: farmSections[0]?.farmName ?? '',
            sectionCount: farmSections.filter((section) => !section.isEmpty).length,
            barns: sortAndGroupSections(farmSections, key),
        };
    });
}
